import React, { useState } from 'react';
import { Users, Plus, UserMinus, Loader2 } from 'lucide-react';
import { BillingAccessCoreProps } from '../widget/types';
import { useWidgetTheme, mainAppTheme } from '../widget/useWidgetTheme';

const BillingAccessCore: React.FC<BillingAccessCoreProps> = ({
  billingAccess,
  loading,
  error,
  mode,
  theme: themeOverrides,
  onGrantAccess,
  onRevokeAccess,
  onSuccess,
  onError,
}) => {
  const widgetTheme = useWidgetTheme();
  const baseTheme = mode === 'modal' ? mainAppTheme : widgetTheme;
  const theme = themeOverrides ? { ...baseTheme, ...themeOverrides } : baseTheme;

  // Local UI state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [addBtnHover, setAddBtnHover] = useState(false);
  const [cancelBtnHover, setCancelBtnHover] = useState(false);

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; id: number; email: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleGrantAccess = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newEmail.trim()) {
      onError?.('Please enter an email address');
      return;
    }

    setAdding(true);
    try {
      await onGrantAccess(newEmail.trim());
      setNewEmail('');
      setShowAddForm(false);
      onSuccess?.('Billing admin added successfully');
    } catch (err: any) {
      onError?.(err.message || 'Failed to add billing admin');
    } finally {
      setAdding(false);
    }
  };

  const handleRevokeAccess = async () => {
    if (!deleteModal) return;

    setDeleting(true);
    try {
      await onRevokeAccess(deleteModal.id, deleteModal.email);
      onSuccess?.('Billing admin removed');
      setDeleteModal(null);
    } catch (err: any) {
      onError?.(err.message || 'Failed to remove billing admin');
      setDeleteModal(null);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString();
  };

  // Error state
  if (error && !loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          backgroundColor: theme.colors.errorBg,
          border: `1px solid ${theme.colors.error}`,
          borderRadius: theme.radius.md,
          padding: '16px',
        }}
      >
        <span style={{ color: theme.colors.error, flexShrink: 0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </span>
        <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: theme.colors.error }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: theme.fontSans, color: theme.colors.text }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: theme.radius.md,
            backgroundColor: theme.colors.primaryLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Users size={20} strokeWidth={1.5} color={theme.colors.primary} />
        </div>
        <div>
          <h2
            style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: theme.colors.text,
              margin: 0,
              letterSpacing: '-0.025em',
            }}
          >
            Billing Admins
          </h2>
        </div>
      </div>

      {/* Info Callout */}
      <div
        style={{
          backgroundColor: theme.colors.bgAlt,
          borderLeft: `4px solid ${theme.colors.primary}`,
          borderRadius: `0 ${theme.radius.md} ${theme.radius.md} 0`,
          padding: '14px 16px',
          marginBottom: '24px',
        }}
      >
        <p style={{ fontSize: '0.875rem', color: theme.colors.text, margin: 0, lineHeight: 1.5 }}>
          Grant workspace administrators access to view billing and subscription information.
        </p>
      </div>

      {/* Add Form or Button */}
      {showAddForm ? (
        <form
          onSubmit={handleGrantAccess}
          style={{
            backgroundColor: theme.colors.bgAlt,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.radius.md,
            padding: '16px',
            marginBottom: '24px',
          }}
        >
          <label
            style={{
              display: 'block',
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: theme.colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '8px',
            }}
          >
            Admin Email or Login
          </label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="user@example.com or username"
              disabled={adding}
              autoFocus
              style={{
                flex: 1,
                padding: '10px 12px',
                backgroundColor: theme.colors.bg,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.radius.sm,
                fontSize: '0.875rem',
                color: theme.colors.text,
                outline: 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = theme.colors.primary;
                e.target.style.boxShadow = `0 0 0 3px ${theme.colors.primaryLight}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = theme.colors.border;
                e.target.style.boxShadow = 'none';
              }}
            />
            <button
              type="submit"
              disabled={adding}
              onMouseEnter={() => setAddBtnHover(true)}
              onMouseLeave={() => setAddBtnHover(false)}
              style={{
                padding: '10px 18px',
                backgroundColor: adding ? theme.colors.border : addBtnHover ? theme.colors.primaryHover : theme.colors.primary,
                color: adding ? theme.colors.textMuted : '#ffffff',
                border: 'none',
                borderRadius: theme.radius.md,
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: adding ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {adding && <Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />}
              {adding ? 'Adding...' : 'Add Admin'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setNewEmail('');
              }}
              disabled={adding}
              onMouseEnter={() => setCancelBtnHover(true)}
              onMouseLeave={() => setCancelBtnHover(false)}
              style={{
                padding: '10px 18px',
                backgroundColor: cancelBtnHover ? theme.colors.bgAlt : theme.colors.bg,
                color: cancelBtnHover ? theme.colors.text : theme.colors.textTertiary,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.radius.md,
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: adding ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          onMouseEnter={() => setAddBtnHover(true)}
          onMouseLeave={() => setAddBtnHover(false)}
          style={{
            marginBottom: '24px',
            padding: '10px 18px',
            backgroundColor: addBtnHover ? theme.colors.primaryHover : theme.colors.primary,
            color: '#ffffff',
            border: 'none',
            borderRadius: theme.radius.md,
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'background-color 0.15s',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Plus size={16} strokeWidth={2} />
          Add Billing Admin
        </button>
      )}

      {/* Access List */}
      {loading ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px',
            gap: '12px',
          }}
        >
          <Loader2 size={24} strokeWidth={2} color={theme.colors.primary} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ color: theme.colors.textTertiary, fontSize: '0.875rem' }}>Loading...</span>
        </div>
      ) : (
        <div
          style={{
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.radius.md,
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: theme.colors.bgAlt }}>
                {['Email / Login', 'Added', 'Last Accessed', ''].map((header, idx) => (
                  <th
                    key={header || idx}
                    style={{
                      padding: '12px 16px',
                      textAlign: idx === 3 ? 'right' : 'left',
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      color: theme.colors.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: `1px solid ${theme.colors.border}`,
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {billingAccess.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: theme.colors.textTertiary,
                      fontSize: '0.875rem',
                    }}
                  >
                    No billing admins added yet
                  </td>
                </tr>
              ) : (
                billingAccess.map((access) => (
                  <tr
                    key={access.id}
                    style={{
                      borderTop: `1px solid ${theme.colors.border}`,
                      backgroundColor: hoveredRow === access.id ? theme.colors.bgAlt : 'transparent',
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={() => setHoveredRow(access.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <td
                      style={{
                        padding: '14px 16px',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: theme.colors.text,
                      }}
                    >
                      {access.pod_user_email}
                    </td>
                    <td
                      style={{
                        padding: '14px 16px',
                        fontSize: '0.875rem',
                        color: theme.colors.textTertiary,
                      }}
                    >
                      {formatDate(access.created_at)}
                    </td>
                    <td
                      style={{
                        padding: '14px 16px',
                        fontSize: '0.875rem',
                        color: theme.colors.textTertiary,
                      }}
                    >
                      {formatDate(access.last_verified_at)}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      <RemoveButton
                        onClick={() => setDeleteModal({ open: true, id: access.id, email: access.pod_user_email })}
                        theme={theme}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal?.open && (
        <DeleteModal
          email={deleteModal.email}
          deleting={deleting}
          onConfirm={handleRevokeAccess}
          onClose={() => setDeleteModal(null)}
          theme={theme}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// Remove Button Component
const RemoveButton: React.FC<{ onClick: () => void; theme: any }> = ({ onClick, theme }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        color: theme.colors.error,
        background: hovered ? theme.colors.errorBg : 'none',
        border: 'none',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.8125rem',
        padding: '6px 10px',
        borderRadius: theme.radius.sm,
        transition: 'background-color 0.15s',
      }}
    >
      <UserMinus size={14} strokeWidth={1.5} />
      Remove
    </button>
  );
};

// Delete Modal Component
const DeleteModal: React.FC<{
  email: string;
  deleting: boolean;
  onConfirm: () => void;
  onClose: () => void;
  theme: any;
}> = ({ email, deleting, onConfirm, onClose, theme }) => {
  const [confirmBtnHover, setConfirmBtnHover] = useState(false);
  const [cancelBtnHover, setCancelBtnHover] = useState(false);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !deleting) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: theme.colors.bg,
          borderRadius: theme.radius.lg,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: `1px solid ${theme.colors.border}`,
          maxWidth: '440px',
          width: '90%',
          overflow: 'hidden',
          fontFamily: theme.fontSans,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${theme.colors.border}` }}>
          <h3 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 600, color: theme.colors.text }}>
            Remove Billing Admin
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: theme.colors.error, fontWeight: 500 }}>
            This action cannot be undone
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '20px 24px' }}>
          <div
            style={{
              backgroundColor: theme.colors.bgAlt,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.radius.md,
              padding: '14px 16px',
              marginBottom: '16px',
            }}
          >
            <p
              style={{
                fontSize: '0.6875rem',
                fontWeight: 600,
                color: theme.colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                margin: '0 0 4px 0',
              }}
            >
              Admin to Remove
            </p>
            <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: theme.colors.text, margin: 0 }}>{email}</p>
          </div>

          <p style={{ fontSize: '0.875rem', color: theme.colors.textMuted, margin: 0, lineHeight: 1.5 }}>
            This user will no longer be able to view billing information, manage payment methods, or access subscription
            settings for this workspace.
          </p>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: `1px solid ${theme.colors.border}`,
            backgroundColor: theme.colors.bgAlt,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          <button
            onClick={onClose}
            disabled={deleting}
            onMouseEnter={() => setCancelBtnHover(true)}
            onMouseLeave={() => setCancelBtnHover(false)}
            style={{
              padding: '10px 20px',
              backgroundColor: cancelBtnHover && !deleting ? theme.colors.bgAlt : theme.colors.bg,
              color: cancelBtnHover && !deleting ? theme.colors.text : theme.colors.textTertiary,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.radius.md,
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: deleting ? 'not-allowed' : 'pointer',
              opacity: deleting ? 0.5 : 1,
              transition: 'all 0.15s',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            onMouseEnter={() => setConfirmBtnHover(true)}
            onMouseLeave={() => setConfirmBtnHover(false)}
            style={{
              padding: '10px 20px',
              backgroundColor: deleting ? '#fecaca' : confirmBtnHover ? '#b91c1c' : theme.colors.error,
              color: deleting ? '#f87171' : '#ffffff',
              border: 'none',
              borderRadius: theme.radius.md,
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: deleting ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.15s',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {deleting && <Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />}
            {deleting ? 'Removing...' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillingAccessCore;
